import { FC, useState } from "react";
import Table from "./Table";
import Summary from "./TableSummary";
import Tabs from "./Components/Tabs/Tabs";
import Tab from "./Components/Tabs/Tab";
import debounce from "debounce";

const ThreeDoorSolver = () => {
  const [choses, setChooses] = useState<strategy[]>([
    {
      name: "Always Old",
      choses: generateArray(100, 1, false),
      sel: 1,
      chang: false,
    },
    {
      name: "Middle Random",
      choses: generateArray(100, 1, () => Math.random() > 0.5),
      sel: 1,
      chang: () => Math.random() > 0.5,
    },
    {
      name: "Fully Random",
      choses: generateArray(100, () => Math.floor(Math.random() * 3), () => Math.random() > 0.5),
      sel: () => Math.floor(Math.random() * 3),
      chang: () => Math.random() > 0.5,
    },
    {
      name: "Always Change",
      choses: generateArray(100, 1, true),
      sel: 1,
      chang: true,
    },
  ]);

  return (
    <Tabs 
      className="w-full"
      buttonsSectionProps={{
        className:"flex flex-wrap justify-center"
      }}
    >
      {
        choses.map( (ch, i) => (
            <Tab 
              key={`ch-${i}`}
              label={`${ch.name}`} 
              className="flex flex-col rounded-xl"
            >
              <div 
                className="
                  flex bg-blue-dark px-5 rounded
                  flex-col 
                  sm:flex-row sm:justify-between
                  xl:px-10 gap-x-20 mb-12 mt-8
                "
              >
                <h1 className="text-lg md:text-2xl capitalize py-5">Strategy: {ch.name}</h1>
                <SimulationSizeInput setChooses={setChooses} strategyIndex={i}/>
              </div>
              <div className="flex flex-col xl:flex-row-reverse xl:justify-stretch gap-10">
                <Summary key={`s-${i}`} choses={ch.choses} className="w-full"/>
                <Table key={`t-${i}`} choses={ch.choses} name={ch.name} className="w-full lg:min-w-max" />
              </div>
            </Tab>
          ) 
        )
      }
    </Tabs>
  );
}

type SimulationSizeInputProps = {
  setChooses: React.Dispatch<React.SetStateAction<strategy[]>>;
  strategyIndex: number,
}

const SimulationSizeInput: FC<SimulationSizeInputProps> = ({
  setChooses,
  strategyIndex
}) => {

  const update = 
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) => {
          setChooses(prev => {
            prev[strategyIndex] = {
              ...prev[strategyIndex],
              choses: generateArray(+e.target.value, prev[strategyIndex].sel, prev[strategyIndex].chang)};
            return [...prev];
          })
      },
      500
    )

  const changeOther = (otherId: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      (document.getElementById(otherId) as HTMLInputElement).value = e.target.value;
    }

  const changeOtherAndUpdate = (otherId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    changeOther(otherId)(e);
    update(e);
  }


  return (
    <div className="space-x-2 flex items-center py-4 xl:w-112 lg:w-96 md:72 sm:w-72 w-full">
      <input 
        type="range" 
        id={`i-s-${strategyIndex}`}
        min={0} 
        max={1000} 
        defaultValue={100}
        className="accent-red w-full"
        onChange={changeOtherAndUpdate(`i-n-${strategyIndex}`)} 
      />
      <input 
        id={`i-n-${strategyIndex}`} 
        type="number" 
        min={0} 
        className="w-20 h-min px-2 py-1 bg-black text-white rounded-xl"
        defaultValue={100}
        onChange={changeOtherAndUpdate(`i-s-${strategyIndex}`)} 
      />
    </div>
  );
}

type strategy = {
  choses: choose[];
  name: string;
  chang: changeFn;
  sel: selectFn;
}

export const DOORS_COUNT = 3;
export const DOORS_INDEXES = Array.from( Array(DOORS_COUNT).keys() ); 

export type secretType = [
  door,
  door,
  door,
];

export type choose = {
  id: number,
  secret: secretType,
  answer: answer,
  dateWhenComplete?: Date,
}
export type answer = {
  select: number,
  openWrong: number,
  change: boolean
}

export const defaultAnswer = {
  change: false,
  openWrong: -1,
  select: -1,
};

export const DOOR = {
  right: "right",
  wrong: "wrong"
} as const;
export type door = keyof typeof DOOR;

export type simFn = (() => boolean);
export type compFn = (({index, selected, hint}:{index?: number, selected?: number, hint?:number}) => boolean);

export const generateSecret = (rightDoor: number) => {
  return DOORS_INDEXES.map( index => 
    index === rightDoor 
      ? DOOR.right 
      : DOOR.wrong
  ) as secretType;
}

type changeFn = boolean | simFn | compFn;
type selectFn = number | ((settings: {index?: number, prev?: number, hint?: number}) => number);
export const generateArray = (size: number, select: selectFn, change: changeFn) => (
    Array
      .from( Array(size).keys() )
      .map<choose>( elem => ({
        id: elem,
        secret: generateSecret(randomDoor()),
        answer: defaultAnswer,
      }))
      .map<choose>( (elem, index) => {

        const selected = typeof select === "number" 
          ? select
          : select({index, prev: elem.answer.select, hint: elem.answer.openWrong});

        const hint = wrongOpenDoor(elem.secret, selected);

        return (
          {
            ...elem,
            answer: {
              select: selected,
              openWrong: hint,
              change: changeDoor(change, index, selected, hint)
            },
          }
        )
      }
      )
);

export const randomDoor = () => Math.floor(Math.random() * 3)

export const isSimFn = (fn: simFn | compFn): fn is simFn => fn.length === 0;

export const changeDoor = (change: boolean | simFn | compFn, index: number, selected:number, hint:number) => {
  return typeof change === "boolean" 
    ? change 
    : isSimFn(change)
      ? change()
      : change({index, selected, hint}) 
}

export const wrongOpenDoor = (secret: secretType, selected: number): number => {
  const wrongNotSelected = secret
    .map((s, i) => s === "wrong" ? i : -1)
    .filter( i => i !== selected)
    .filter( i => i !== -1)
  
  const random = Math.floor(Math.random() * (wrongNotSelected.length));
  
  return wrongNotSelected[random];
}

export const visualizeDoors = (c: choose) =>
{
  return c.secret.map((d, i) => visualizeDoor(d, c.id, i));
}
export const visualizeDoor = (door: door, chooseId: number, doorId: number) => {
  switch (door) {
    case "right":
      return <span key={`vis-sel-${chooseId}-${doorId}`} className="text-green font-symbol">■</span>;
    case "wrong":
      return <span key={`vis-sel-${chooseId}-${doorId}`} className="text-red font-symbol">▩</span>;
  }
}

export const visualizeSelected = (c: choose) => {
  return c.secret.map ((d, i) => {
    if (i === c.answer.select) 
      return <span key={`vis-sel-${c.id}-${i}`} className="font-symbol">▣</span>
    return <span key={`vis-sel-${c.id}-${i}`} className="font-symbol">□</span>
  })
}

export const visualizeWrongAndSelect = (c: choose) => {
  return c.secret.map ((d, i) => {
    if (i === c.answer.select) 
      return <span key={`vis-sel-${c.id}-${i}`} className="font-symbol">▣</span>
    if (i === c.answer.openWrong) 
      return visualizeDoor(d, c.id, i)
    return <span key={`vis-sel-${c.id}-${i}`} className="font-symbol">□</span>
  })
}

export const finalChoice = (c: choose): choose => {
  return {
      ...c,
      answer: {
        select: finalDoor(c),
        change: false,
        openWrong: c.answer.openWrong
      },
    }
} 
export const finalDoor = (c: choose): number => {
  return c.answer.change 
    ? [0, 1, 2]
      .filter((e) => e !== c.answer.select)
      .filter((e) => e !== c.answer.openWrong)[0]
    : c.answer.select;
}

export default ThreeDoorSolver;
